import { humanize } from 'inflected';
import { AudiencePenetrationRow } from '../audience-penetration-types';
import { computeMetrics } from './format';

export interface AudiencePenetrationRowWithMetrics extends AudiencePenetrationRow {
  audienceReachPct: number | null;
  audienceConversionPct: number | null;
  conversionRatePct: number | null;
}

export const withMetrics = (rows: AudiencePenetrationRow[]): AudiencePenetrationRowWithMetrics[] =>
  rows.map(row => ({ ...row, ...computeMetrics(row) }));

// `segmentType` acronyms don't humanize nicely on their own ("amc" -> "Amc") — spell out the
// known ones; everything else falls back to `humanize`, which handles the common
// hyphenated/underscored cases fine ("in-market" -> "In market").
const SEGMENT_TYPE_LABEL_OVERRIDES: Record<string, string> = { amc: 'AMC' };

export const getSegmentTypeOptions = (
  rows: AudiencePenetrationRow[]
): { value: string; label: string }[] =>
  Array.from(new Set(rows.map(r => r.segmentType)))
    .sort()
    .map(value => ({
      value,
      label: SEGMENT_TYPE_LABEL_OVERRIDES[value] ?? humanize(value.replace(/-/g, '_'))
    }));

// Reqs doc: the by-name search runs before the segment-type/top-N filters, so a search that
// narrows below the top-N cutoff still surfaces matches that would otherwise be excluded.
// Segment types defaults to every option selected (not empty) — see AudiencePenetration.tsx — so
// filtering is a plain inclusion check throughout; an empty selection now genuinely means "match
// nothing" (the user unchecked every type), not "no filter applied." `nameFilter` is FilterBar's
// own computed predicate (see AudiencePenetrationFilters.tsx), not a raw search string.
export const filterRows = (
  rows: AudiencePenetrationRowWithMetrics[],
  segmentTypes: string[],
  nameFilter: (row: AudiencePenetrationRowWithMetrics) => boolean,
  hideOverfullReach = false
): AudiencePenetrationRowWithMetrics[] =>
  rows
    .filter(nameFilter)
    .filter(r => segmentTypes.includes(r.segmentType))
    .filter(r => !hideOverfullReach || !exceedsFullReach(r));

// Reach above 100% of a segment's user count means reach outgrew the segment's (small or stale)
// user count — these rows are inaccurate and sort to the top of the dashboard. Rows with no
// reach % at all (non-FSI clients) are kept, since there's nothing to say they're inaccurate.
export const exceedsFullReach = (row: AudiencePenetrationRowWithMetrics): boolean =>
  row.audienceReachPct !== null && row.audienceReachPct > 1;

export const sortByDesc = <T>(rows: T[], key: keyof T): T[] =>
  [...rows].sort((a, b) => (Number(b[key]) || 0) - (Number(a[key]) || 0));

// null/0/negative n means "show all" (the reqs doc's "top N OR ALL" toggle).
export const takeTopN = <T>(rows: T[], n: number | null): T[] =>
  n === null || n <= 0 ? rows : rows.slice(0, n);
